import React, { useMemo, useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Space,
  Divider,
  message,
  Empty,
} from 'antd';
import {
  FileTextOutlined,
  DollarOutlined,
  MergeCellsOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const statusColors = {
  pending: 'gold',
  confirmed: 'blue',
  in_progress: 'cyan',
  completed: 'green',
  cancelled: 'red',
};

const statusLabels = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const formatMoney = (value) => `NPR ${Number(value || 0).toFixed(2)}`;

const VAT_RATE = 13;

const CombinedBilling = () => {
  const [searchParams] = useSearchParams();
  const customerParam = searchParams.get('customer_id');
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [selectedBookingIds, setSelectedBookingIds] = useState([]);
  const [selectedOfflineSaleIds, setSelectedOfflineSaleIds] = useState([]);
  const [billingType, setBillingType] = useState('vat');

  const { data: ordersResponse, isLoading: loadingOrders } = useQuery(
    ['billing-orders-for-combined', customerParam],
    async () => {
      const response = await api.get('/admin/billing/orders-for-billing', {
        params: {
          per_page: -1,
          ...(customerParam ? { customer_id: customerParam } : {}),
        },
      });
      return response.data?.data || [];
    },
  );

  const { data: bookingsResponse, isLoading: loadingBookings } = useQuery(
    ['billing-bookings-for-combined', customerParam],
    async () => {
      const response = await api.get('/admin/billing/bookings-for-billing', {
        params: customerParam ? { customer_id: customerParam } : {},
      });
      return response.data?.data || [];
    },
  );

  const { data: offlineSalesResponse = [], isLoading: loadingOfflineSales } = useQuery(
    ['billing-offline-sales-for-combined', customerParam],
    async () => {
      const response = await api.get('/admin/offline-sales/for-billing', {
        params: customerParam ? { customer_id: customerParam } : {},
      });
      return response.data?.data || [];
    },
  );

  const orders = Array.isArray(ordersResponse) ? ordersResponse : [];
  const bookings = Array.isArray(bookingsResponse) ? bookingsResponse : [];
  const offlineSales = Array.isArray(offlineSalesResponse) ? offlineSalesResponse : [];

  const lineItems = useMemo(() => {
    const items = [];

    for (const order of orders) {
      if (!selectedOrderIds.includes(order.id)) continue;
      for (const item of order.items || []) {
        const type = item.product?.type || 'product';
        items.push({
          key: `order-${order.id}-${item.id}`,
          sourceType: 'order',
          sourceId: order.id,
          sourceLabel: `Order ${order.order_number || `#${order.id}`}`,
          name: item.product?.name || item.item_name || 'Unknown Item',
          type,
          quantity: item.quantity,
          unitPrice: Number(item.price_at_order || 0),
          total: Number(item.quantity || 0) * Number(item.price_at_order || 0),
        });
      }
    }

    for (const booking of bookings) {
      if (!selectedBookingIds.includes(booking.id)) continue;
      const amount = Number(booking.total_amount || 0);
      items.push({
        key: `booking-${booking.id}`,
        sourceType: 'booking',
        sourceId: booking.id,
        sourceLabel: `Booking #${booking.id}`,
        name: booking.product?.name || `Booking #${booking.id}`,
        type: 'service',
        quantity: 1,
        unitPrice: amount,
        total: amount,
      });
    }

    for (const sale of offlineSales) {
      if (!selectedOfflineSaleIds.includes(sale.id)) continue;
      (sale.line_items || []).forEach((item, index) => {
        const type = item.type || 'product';
        items.push({
          key: `offline-${sale.id}-${index}`,
          sourceType: 'offline_sale',
          sourceId: sale.id,
          sourceLabel: `Offline ${sale.sale_number}`,
          name: item.name || 'Item',
          type,
          quantity: Number(item.quantity || 1),
          unitPrice: Number(item.unit_price || 0),
          total: Number(item.total || 0),
        });
      });
    }

    return items;
  }, [orders, bookings, offlineSales, selectedOrderIds, selectedBookingIds, selectedOfflineSaleIds]);

  const totals = useMemo(() => {
    const productSubtotal = lineItems
      .filter((i) => i.type === 'product')
      .reduce((sum, i) => sum + i.total, 0);
    const serviceSubtotal = lineItems
      .filter((i) => i.type === 'service')
      .reduce((sum, i) => sum + i.total, 0);

    const vatRate = VAT_RATE;
    const vatOnServices = billingType === 'vat_all';
    let vatAmount = 0;
    let taxableAmount = productSubtotal;
    let subtotal = productSubtotal + serviceSubtotal;
    let totalAmount = subtotal;

    const taxableBase = vatOnServices ? productSubtotal + serviceSubtotal : productSubtotal;

    if ((billingType === 'vat' || billingType === 'vat_all') && taxableBase > 0) {
      const baseBeforeVat = taxableBase / (1 + vatRate / 100);
      vatAmount = taxableBase - baseBeforeVat;
      taxableAmount = baseBeforeVat;
      subtotal = baseBeforeVat + (vatOnServices ? 0 : serviceSubtotal);
      totalAmount = taxableBase + (vatOnServices ? 0 : serviceSubtotal);
    }

    return {
      productSubtotal,
      serviceSubtotal,
      taxableAmount,
      vatAmount,
      subtotal,
      totalAmount,
      vatRate,
      hasProducts: productSubtotal > 0,
      vatOnServices,
    };
  }, [lineItems, billingType]);

  const generateBillMutation = useMutation(
    () =>
      api.post('/admin/billing/combined/generate-bill', {
        order_ids: selectedOrderIds,
        booking_ids: selectedBookingIds,
        offline_sale_ids: selectedOfflineSaleIds,
        billing_type: billingType,
      }),
    {
      onSuccess: (response) => {
        const invoice = response.data?.data;
        const msg = response.data?.message || 'Combined bill generated';
        message.success(
          `${msg} — ${invoice?.invoice_number || 'invoice created'}`,
        );
        setSelectedOrderIds([]);
        setSelectedBookingIds([]);
        setSelectedOfflineSaleIds([]);
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to generate combined bill');
      },
    },
  );

  const orderColumns = [
    {
      title: 'Order',
      key: 'order',
      width: 110,
      render: (_, record) => record.order_number || `ORD-${String(record.id).padStart(5, '0')}`,
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 180,
      render: (_, record) => (
        <div>
          <Text strong>{record.customer?.full_name || record.customer?.name || '-'}</Text>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>{record.customer?.email || ''}</div>
        </div>
      ),
    },
    {
      title: 'Items',
      key: 'items',
      ellipsis: true,
      render: (_, record) => {
        const names = (record.items || []).map((i) => i.product?.name || i.item_name);
        return <Text>{names.join(', ') || '—'}</Text>;
      },
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      width: 110,
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '-'),
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      width: 100,
      align: 'right',
      render: formatMoney,
    },
    {
      title: 'Status',
      key: 'billed',
      width: 90,
      render: (_, record) =>
        record.invoices_count > 0 ? (
          <Tag color="green">Billed</Tag>
        ) : (
          <Tag color="orange">Unbilled</Tag>
        ),
    },
  ];

  const bookingColumns = [
    {
      title: 'Booking',
      key: 'booking',
      width: 100,
      render: (_, record) => `#${record.id}`,
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 180,
      render: (_, record) => (
        <div>
          <Text strong>{record.user?.name || '-'}</Text>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>{record.user?.email || ''}</div>
        </div>
      ),
    },
    {
      title: 'Service',
      key: 'service',
      ellipsis: true,
      render: (_, record) => record.product?.name || `#${record.product_id}`,
    },
    {
      title: 'Schedule',
      key: 'schedule',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.booking_date ? dayjs(record.booking_date).format('DD MMM YYYY') : '-'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.booking_time ? dayjs(record.booking_time).format('h:mm A') : '-'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      width: 100,
      align: 'right',
      render: formatMoney,
    },
    {
      title: 'Status',
      key: 'billed',
      width: 90,
      render: (_, record) =>
        record.invoices_count > 0 ? (
          <Tag color="green">Billed</Tag>
        ) : (
          <Tag color="orange">Unbilled</Tag>
        ),
    },
  ];

  const offlineSaleColumns = [
    {
      title: 'Sale #',
      dataIndex: 'sale_number',
      key: 'sale_number',
      width: 150,
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 180,
      render: (_, record) => (
        <div>
          <Text strong>{record.customer_name || record.customer?.name || '-'}</Text>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>
            {record.customer_phone || record.customer?.phone_number || ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Items',
      key: 'items',
      ellipsis: true,
      render: (_, record) => {
        const names = (record.line_items || []).map((i) => i.name);
        return <Text>{names.join(', ') || '—'}</Text>;
      },
    },
    {
      title: 'Date',
      dataIndex: 'purchase_date',
      width: 110,
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '-'),
    },
    {
      title: 'Payment',
      dataIndex: 'payment_method',
      width: 100,
      render: (v) => (v ? <Tag>{String(v).toUpperCase()}</Tag> : '-'),
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      width: 100,
      align: 'right',
      render: formatMoney,
    },
  ];

  const previewColumns = [
    {
      title: 'Source',
      key: 'source',
      width: 130,
      render: (_, record) => <Text type="secondary">{record.sourceLabel}</Text>,
    },
    {
      title: 'Item',
      dataIndex: 'name',
      ellipsis: true,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      width: 90,
      render: (type) => (
        <Tag color={type === 'service' ? 'purple' : 'blue'}>
          {type === 'service' ? 'Service' : 'Product'}
        </Tag>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      width: 60,
      align: 'right',
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      width: 100,
      align: 'right',
      render: (value, record) => {
        let price = Number(value || 0);
        if (
          (billingType === 'vat' && record.type === 'product') ||
          (billingType === 'vat_all')
        ) {
          price = price / (1 + VAT_RATE / 100);
        }
        return formatMoney(price);
      },
    },
    {
      title: 'Total',
      dataIndex: 'total',
      width: 110,
      align: 'right',
      render: (value, record) => {
        let total = Number(value || 0);
        if (
          (billingType === 'vat' && record.type === 'product') ||
          (billingType === 'vat_all')
        ) {
          total = total / (1 + VAT_RATE / 100);
        }
        return formatMoney(total);
      },
    },
  ];

  const selectedOrders = orders.filter((o) => selectedOrderIds.includes(o.id));
  const selectedBookings = bookings.filter((b) => selectedBookingIds.includes(b.id));

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            <MergeCellsOutlined /> Combined Bill — Products + Services + Offline
          </Title>
        <Text type="secondary">
          Merge product orders, service bookings, and offline counter sales into a single invoice
          for one customer.
        </Text>
      </div>

      <Card
        title="Select Orders (Products)"
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Table
          rowKey="id"
          size="small"
          loading={loadingOrders}
          columns={orderColumns}
          dataSource={orders}
          rowSelection={{
            selectedRowKeys: selectedOrderIds,
            onChange: setSelectedOrderIds,
            preserveSelectedRowKeys: true,
            getCheckboxProps: (record) => ({
              disabled: (record.invoices_count || 0) > 0,
            }),
          }}
          pagination={{ pageSize: 6, showSizeChanger: false }}
        />
      </Card>

      <Card
        title="Select Bookings (Services)"
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Table
          rowKey="id"
          size="small"
          loading={loadingBookings}
          columns={bookingColumns}
          dataSource={bookings}
          rowSelection={{
            selectedRowKeys: selectedBookingIds,
            onChange: setSelectedBookingIds,
            preserveSelectedRowKeys: true,
            getCheckboxProps: (record) => ({
              disabled: (record.invoices_count || 0) > 0,
            }),
          }}
          pagination={{ pageSize: 6, showSizeChanger: false }}
        />
      </Card>

      <Card
        title="Select Offline Sales (Shop Counter)"
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Table
          rowKey="id"
          size="small"
          loading={loadingOfflineSales}
          columns={offlineSaleColumns}
          dataSource={offlineSales}
          rowSelection={{
            selectedRowKeys: selectedOfflineSaleIds,
            onChange: setSelectedOfflineSaleIds,
            preserveSelectedRowKeys: true,
            getCheckboxProps: (record) => ({
              disabled: record.status === 'billed',
            }),
          }}
          pagination={{ pageSize: 6, showSizeChanger: false }}
        />
      </Card>

      <Card
        title="Bill Preview"
        size="small"
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <Card
              hoverable
              size="small"
              style={{
                border: billingType === 'standard' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                cursor: 'pointer',
              }}
              onClick={() => setBillingType('standard')}
            >
              <Space size={4}>
                <FileTextOutlined style={{ color: billingType === 'standard' ? '#1890ff' : '#999' }} />
                <Text strong={billingType === 'standard'}>Standard</Text>
              </Space>
            </Card>
            <Card
              hoverable
              size="small"
              style={{
                border: billingType === 'vat' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                cursor: 'pointer',
              }}
              onClick={() => setBillingType('vat')}
            >
              <Space size={4}>
                <DollarOutlined style={{ color: billingType === 'vat' ? '#1890ff' : '#999' }} />
                <Text strong={billingType === 'vat'}>VAT (13% products)</Text>
              </Space>
            </Card>
            <Card
              hoverable
              size="small"
              style={{
                border: billingType === 'vat_all' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                cursor: 'pointer',
              }}
              onClick={() => setBillingType('vat_all')}
            >
              <Space size={4}>
                <DollarOutlined style={{ color: billingType === 'vat_all' ? '#1890ff' : '#999' }} />
                <Text strong={billingType === 'vat_all'}>VAT (13% products + services)</Text>
              </Space>
            </Card>
          </Space>
        }
      >
        {lineItems.length === 0 ? (
          <Empty description="Select at least one order, booking, or offline sale to preview the bill" />
        ) : (
          <>
            <Table
              rowKey="key"
              size="small"
              columns={previewColumns}
              dataSource={lineItems}
              pagination={false}
            />
            <Divider />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Space direction="vertical" size={4} style={{ width: 300, textAlign: 'right' }}>
                {billingType !== 'standard' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text type="secondary">Taxable Amount</Text>
                    <Text>{formatMoney(totals.taxableAmount)}</Text>
                  </div>
                )}
                {billingType !== 'vat_all' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text type="secondary">Tax Exempt Amount</Text>
                    <Text>{formatMoney(totals.serviceSubtotal)}</Text>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">Subtotal</Text>
                  <Text>{formatMoney(totals.subtotal)}</Text>
                </div>
                {billingType !== 'standard' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text type="secondary">
                      Tax Amount{['vat', 'vat_all'].includes(billingType) ? ` (VAT ${totals.vatRate}%)` : ''}
                    </Text>
                    <Text>{formatMoney(totals.vatAmount)}</Text>
                  </div>
                )}
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong>
                    Total Amount ({selectedOrders.length} order(s), {selectedBookings.length} booking(s),{' '}
                    {selectedOfflineSaleIds.length} offline sale(s))
                  </Text>
                  <Text strong>{formatMoney(totals.totalAmount)}</Text>
                </div>
              </Space>
            </div>
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<FileTextOutlined />}
                loading={generateBillMutation.isLoading}
                disabled={lineItems.length === 0}
                onClick={() => generateBillMutation.mutate()}
              >
                Generate Combined Bill
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default CombinedBilling;
